import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import './Card.css';

/**
 * Card component
 *
 * Props:
 *   title       — card heading
 *   description — body text
 *   icon        — Lucide icon component
 *   iconLabel   — accessible label for the icon (e.g. "Life Insurance icon")
 *   link        — internal route string → renders <Link>
 *   href        — external URL string   → renders <a target="_blank">
 *   linkLabel   — custom CTA text (default: "Learn more")
 *   onClick     — if no link/href, renders a <button> card-link
 *   variant     — '' | 'featured' | 'flat' | 'ghost'
 *   badge       — optional top-right badge text (e.g. "Popular")
 *   isLoading   — renders skeleton state when true
 *   className   — extra classes for the outer article
 */
const Card = forwardRef(({
  title,
  description,
  icon: Icon,
  iconLabel,
  link,
  href,
  linkLabel = 'Learn more',
  onClick,
  variant   = '',
  badge,
  isLoading = false,
  className = '',
  ...rest
}, ref) => {

  // ── Loading skeleton ─────────────────────────────────────────────
  if (isLoading) {
    return (
      <article
        ref={ref}
        className={`card card--skeleton${variant ? ` card--${variant}` : ''}${className ? ` ${className}` : ''}`}
        aria-busy="true"
        aria-label="Loading card"
        {...rest}
      >
        <div className="card-icon skeleton" aria-hidden="true" />
        <div className="card-body">
          <div className="skeleton skeleton-heading" />
          <div className="skeleton skeleton-text" />
          <div className="skeleton skeleton-text" />
        </div>
        <div className="skeleton skeleton-link" />
      </article>
    );
  }

  // ── Determine CTA element ────────────────────────────────────────
  const renderCta = () => {
    const content = (
      <>
        {linkLabel}
        <span className="link-arrow" aria-hidden="true">
          <ArrowRight size={15} />
        </span>
      </>
    );

    // Internal route
    if (link) {
      return (
        <Link to={link} className="card-link">
          {content}
        </Link>
      );
    }

    // External URL
    if (href) {
      return (
        <a
          href={href}
          className="card-link"
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${linkLabel} about ${title} (opens in new tab)`}
        >
          {content}
        </a>
      );
    }

    // Callback only
    if (onClick) {
      return (
        <button
          type="button"
          className="card-link"
          onClick={onClick}
          aria-label={`${linkLabel} about ${title}`}
        >
          {content}
        </button>
      );
    }

    return null;
  };

  // ── Composed class string ────────────────────────────────────────
  const cardClass = [
    'card',
    variant   ? `card--${variant}` : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <article ref={ref} className={cardClass} {...rest}>

      {/* Optional badge */}
      {badge && (
        <span className="card-badge" aria-label={`Badge: ${badge}`}>
          {badge}
        </span>
      )}

      {/* Icon */}
      {Icon && (
        <div
          className="card-icon"
          role="img"
          aria-label={iconLabel || `${title} icon`}
        >
          <Icon size={22} aria-hidden="true" />
        </div>
      )}

      {/* Body */}
      <div className="card-body">
        {title       && <h3>{title}</h3>}
        {description && <p>{description}</p>}
      </div>

      {/* CTA */}
      {renderCta()}

    </article>
  );
});

Card.displayName = 'Card';

export default Card;